import { useState } from 'react'

import { SegmentedControl, type SegmentedControlProps } from '@components/atoms/SegmentedControl'

import { ChoosingControlOrNav } from '../ChoosingControlOrNav'
import type { ComponentDoc, PropDocs } from '../types'

type Props = SegmentedControlProps<string>

const props: PropDocs<Props> = {
  options: {
    type: '{ value: T; label: string; leadingIcon?: IconTypes; count?: number }[]',
    required: true,
    description: 'One entry per choice. A count shows in a small pill after the label.',
  },
  value: { type: 'T', required: true, description: 'The value of the selected option.' },
  onChange: { type: '(value: T) => void', required: true, description: 'Called with the value picked.' },
  ariaLabel: {
    type: 'string',
    required: true,
    description: 'Names the control with what it chooses, for example "Layout".',
  },
  className: { type: 'string', description: 'Extra classes on the track.' },
}

function Example({ options }: { options: Props['options'] }) {
  const [value, setValue] = useState(options[0].value)
  return <SegmentedControl ariaLabel="Example" options={options} value={value} onChange={setValue} />
}

const layouts: Props['options'] = [
  { value: 'card', label: 'Cards', leadingIcon: 'copy-06' },
  { value: 'table', label: 'Table', leadingIcon: 'table' },
]

const scopes: Props['options'] = [
  { value: 'me', label: 'Assigned to me', count: 5 },
  { value: 'groups', label: 'My groups', count: 2 },
  { value: 'all', label: 'All', count: 9 },
]

const doc: ComponentDoc<Props> = {
  slug: 'segmented-control',
  name: 'SegmentedControl',
  tier: 'atom',
  description:
    'Switches between a few views of one area. A white thumb springs along a gray track to the selected option, and a fainter one glides to the option nearest the pointer.',
  importStatement: "import { SegmentedControl } from '@components/atoms/SegmentedControl'",
  sourcePath: 'app/frontend/components/atoms/SegmentedControl.tsx',
  component: SegmentedControl,
  props,
  examples: [
    {
      title: 'With icons',
      render: () => <Example options={layouts} />,
      code: `<SegmentedControl
  ariaLabel="Layout"
  options={[
    { value: 'card', label: 'Cards', leadingIcon: 'copy-06' },
    { value: 'table', label: 'Table', leadingIcon: 'table' },
  ]}
  value={layout}
  onChange={setLayout}
/>`,
    },
    {
      title: 'With counts',
      render: () => <Example options={scopes} />,
      code: `<SegmentedControl
  ariaLabel="Whose callbacks"
  options={[
    { value: 'me', label: 'Assigned to me', count: 5 },
    { value: 'groups', label: 'My groups', count: 2 },
    { value: 'all', label: 'All', count: 9 },
  ]}
  value={scope}
  onChange={setScope}
/>`,
    },
  ],
  usage: {
    do: [
      'Put it at the top of the card or section it switches, or in a toolbar next to a search field or select.',
      'Keep it to two to four short options.',
      'Keep a view that holds a form mounted and hide it, so switching away keeps what was typed.',
      'Swap it for a select on small screens when the labels are long.',
    ],
    dont: [
      'Use it to move between pages or the sections of a page. Use PageHeaderNav or links.',
      'Use it for options that can be combined. Use checkboxes or a multiselect.',
    ],
  },
  choosing: { title: 'Segmented control or page header nav?', content: <ChoosingControlOrNav /> },
  a11y: [
    'It is a group named by ariaLabel. Each option is a button with aria-pressed, and Tab moves between them.',
    'The selected option is shown by the white thumb and a bolder label, not by colour alone.',
    'A count is part of the option text, so it is read with the label.',
    'The hover highlight follows keyboard focus as well as the pointer.',
    'The thumb does not slide when the user asks for reduced motion.',
  ],
}

export default doc
